/**
 * Generates `.vibedrift/context.md`, `.vibedrift/fix-plan.md`, and
 * `.vibedrift/patterns.json` when the user passes --write-context.
 *
 * These files are designed to be committed. They give a repo's dominant
 * patterns a persistent, AI-readable home alongside CLAUDE.md / AGENTS.md.
 */

import { mkdir, writeFile, readFile } from "fs/promises";
import { join } from "path";
import type { ScanResult, Finding, DriftFindingReport } from "../core/types.js";
import { buildFullFixPlanMarkdown, findingKey, buildFixPromptMarkdown, buildUpsellBlock } from "./fix-prompt.js";
import { getVersion } from "../core/version.js";
import { relativeTime } from "./history-diff.js";
import { formatCount } from "./format.js";
import { hasMeaningfulImpact } from "./fix-plan-select.js";

const OUT_DIR = ".vibedrift";

function topImpact(findings: Finding[], n: number): Finding[] {
  return [...findings]
    .filter(hasMeaningfulImpact)
    .sort((a, b) => (b.consistencyImpact ?? 0) - (a.consistencyImpact ?? 0))
    .slice(0, n);
}

function driftByCategory(drift: DriftFindingReport[]): Map<string, DriftFindingReport[]> {
  const map = new Map<string, DriftFindingReport[]>();
  for (const d of drift) {
    const arr = map.get(d.driftCategory) ?? [];
    arr.push(d);
    map.set(d.driftCategory, arr);
  }
  return map;
}

function humanize(cat: string): string {
  return cat.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function buildContextMarkdown(result: ScanResult, projectName: string, isPaid: boolean = false): string {
  const lines: string[] = [];
  const pct = result.maxCompositeScore > 0
    ? (result.compositeScore / result.maxCompositeScore) * 100
    : 0;
  const grade = pct >= 90 ? "A" : pct >= 75 ? "B" : pct >= 50 ? "C" : pct >= 25 ? "D" : "F";

  lines.push(`<!-- Auto-generated by VibeDrift v${getVersion()} — https://vibedrift.ai. Safe to commit. -->`);
  lines.push(`<!-- Refresh with: npx @vibedrift/cli . --write-context -->`);
  lines.push("");
  lines.push(`# VibeDrift Context — ${projectName}`);
  lines.push("");
  lines.push(`- **Last updated:** ${new Date().toISOString()}`);
  lines.push(`- **Vibe Drift Score:** ${result.compositeScore.toFixed(0)}/${result.maxCompositeScore} (${grade})`);
  lines.push(`- **Dominant language:** ${result.context.dominantLanguage ?? "mixed"}`);
  lines.push(`- **Files scanned:** ${result.context.files.length} (${formatCount(result.context.totalLines)} lines)`);
  lines.push("");

  lines.push("## Dominant patterns in this codebase");
  lines.push("");
  const byCat = driftByCategory(result.driftFindings ?? []);
  if (byCat.size === 0) {
    lines.push("_No drift findings — the codebase is internally consistent as measured by the detectors that ran._");
  } else {
    for (const [cat, items] of byCat) {
      const dominant = items[0];
      if (!dominant?.dominantPattern) continue;
      lines.push(
        `- **${humanize(cat)}:** ${dominant.dominantPattern} (${dominant.dominantCount}/${dominant.totalRelevantFiles} files)` +
          (dominant.dominantFiles && dominant.dominantFiles.length > 0
            ? ` — e.g., \`${dominant.dominantFiles[0]}\``
            : ""),
      );
    }
  }
  lines.push("");

  const top = topImpact(result.findings, 10);
  lines.push("## Drift items currently open");
  lines.push("");
  if (top.length === 0) {
    lines.push("_No open drift items with measurable impact._");
  } else {
    top.forEach((f, idx) => {
      const loc = f.locations[0];
      const where = loc ? `${loc.file}${loc.line ? ":" + loc.line : ""}` : "(project-wide)";
      const impact = (f.consistencyImpact ?? 0).toFixed(1);
      const msg = f.message.replace(/^DRIFT:\s*/, "");
      lines.push(`${idx + 1}. \`${where}\` — ${msg} (+${impact}pts consistency if re-aligned)`);
    });
  }
  lines.push("");

  // Recent trajectory — scan-over-scan diff. Lets AI agents see what
  // changed since the last run without re-reading the whole codebase.
  if (result.diff && !result.diff.incomparable) {
    const d = result.diff;
    const resolved = d.driftFindingsDiff.resolved.length + d.findingsDiff.resolved.length;
    const added = d.driftFindingsDiff.new.length + d.findingsDiff.new.length;
    if (resolved > 0 || added > 0 || d.scoreDelta !== 0) {
      lines.push("## Recent trajectory");
      lines.push("");
      lines.push(`- Previous scan: ${relativeTime(d.fromTimestamp)}`);
      if (d.scoreDelta !== 0) {
        const sign = d.scoreDelta > 0 ? "+" : "";
        lines.push(`- Vibe Drift Score delta: **${sign}${d.scoreDelta.toFixed(1)}**`);
      }
      if (resolved > 0) lines.push(`- Resolved since last scan: ${resolved}`);
      if (added > 0) {
        lines.push(`- New findings since last scan: ${added}`);
        for (const nd of d.driftFindingsDiff.new.slice(0, 5)) {
          lines.push(`  - ${nd.message.slice(0, 160)}`);
        }
      }
      lines.push("");
    }
  }

  lines.push("## If you're an AI agent working on this codebase");
  lines.push("");
  lines.push(
    "When modifying files here, match the dominant patterns listed above. Re-run `npx @vibedrift/cli` after your changes to verify the drift has closed and the consistency score has improved." +
      (isPaid
        ? " The per-finding fix prompts in `.vibedrift/fix-plan.md` give you copy-ready context for each open drift item."
        : " Per-finding copy-ready fix prompts are a Pro feature — run `vibedrift upgrade` to generate `.vibedrift/fix-plan.md` for each open drift item."),
  );
  lines.push("");

  lines.push("---");
  lines.push("_Generated by [VibeDrift](https://vibedrift.ai) — drift detection for AI-generated codebases. `npx @vibedrift/cli .`_");
  lines.push("");

  return lines.join("\n");
}

function buildPatternsJson(result: ScanResult): string {
  const byCat = driftByCategory(result.driftFindings ?? []);
  const out: Record<string, {
    dominantPattern: string;
    dominantCount: number;
    totalRelevantFiles: number;
    consistencyScore: number;
    dominantFiles: string[];
    deviatingFiles: string[];
  }> = {};

  for (const [cat, items] of byCat) {
    const d = items[0];
    if (!d) continue;
    out[cat] = {
      dominantPattern: d.dominantPattern ?? "",
      dominantCount: d.dominantCount ?? 0,
      totalRelevantFiles: d.totalRelevantFiles ?? 0,
      consistencyScore: d.consistencyScore ?? 0,
      dominantFiles: d.dominantFiles ?? [],
      deviatingFiles: (d.deviatingFiles ?? []).map((df) => df.path),
    };
  }

  return JSON.stringify(
    {
      generatedBy: `@vibedrift/cli@${getVersion()}`,
      url: "https://vibedrift.ai",
      generatedAt: new Date().toISOString(),
      score: result.compositeScore,
      maxScore: result.maxCompositeScore,
      patterns: out,
    },
    null,
    2,
  );
}

async function noteGitignore(rootDir: string): Promise<string | null> {
  try {
    const gi = await readFile(join(rootDir, ".gitignore"), "utf8");
    if (gi.split("\n").some((l) => l.trim() === ".vibedrift" || l.trim() === ".vibedrift/")) {
      return "`.vibedrift/` is currently in .gitignore — remove that entry if you want to commit the generated context for your team to share.";
    }
    return null;
  } catch {
    return null;
  }
}

export async function writeContextFiles(
  rootDir: string,
  result: ScanResult,
  projectName: string,
  isPaid: boolean = false,
): Promise<{ written: string[]; note: string | null }> {
  const outDir = join(rootDir, OUT_DIR);
  await mkdir(outDir, { recursive: true });

  const top = topImpact(result.findings, 10);
  const ctx = {
    projectName,
    language: result.context.dominantLanguage ?? null,
    fileCount: result.context.files.length,
  };

  // Fix prompts are a paid feature. Free plans get context.md + patterns.json in
  // full (score + dominant patterns + open drift), but fix-plan.md / fix-prompts.md
  // carry an upsell instead of the copy-ready fixes.
  const contextMd = buildContextMarkdown(result, projectName, isPaid);
  const patternsJson = buildPatternsJson(result);
  const upsell = buildUpsellBlock(projectName);
  const fixPlanMd = !isPaid
    ? upsell
    : top.length > 0
      ? buildFullFixPlanMarkdown(top, ctx)
      : "# VibeDrift Fix Plan\n\nNo drift findings with measurable impact — the codebase is well-aligned.\n";
  const perFindingBlocks = !isPaid
    ? upsell
    : top
        .map((f, i) => `<!-- finding ${i + 1}: ${findingKey(f)} -->\n${buildFixPromptMarkdown(f, ctx)}`)
        .join("\n\n---\n\n");

  await Promise.all([
    writeFile(join(outDir, "context.md"), contextMd),
    writeFile(join(outDir, "fix-plan.md"), fixPlanMd),
    writeFile(join(outDir, "patterns.json"), patternsJson),
    writeFile(join(outDir, "fix-prompts.md"), perFindingBlocks || "# VibeDrift Fix Prompts\n\nNo findings to display.\n"),
  ]);

  const note = await noteGitignore(rootDir);
  return {
    written: [
      `${OUT_DIR}/context.md`,
      `${OUT_DIR}/fix-plan.md`,
      `${OUT_DIR}/fix-prompts.md`,
      `${OUT_DIR}/patterns.json`,
    ],
    note,
  };
}
